// built-from: /engagement-setup-workspace v2 impl:engagement-setup-workspace role:implementation
// built-from: /engagement-management-workspace v1 impl:engagement-management-workspace role:implementation
// built-from: /engagement-activity-execution v1 impl:engagement-activity-execution role:implementation
// built-from: /engagement-manage-direct-edit v1 impl:engagement-manage-direct-edit role:implementation
// built-from: /knowledge-files-view v1 impl:knowledge-files-view role:implementation
// built-from: /engagement-context-panel v1 impl:engagement-context-panel role:implementation
import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronLeft, FileText, Plus } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { attachFacilitator } from '../facilitation/ContextualFacilitatorCompanion';
import { surfaceForId } from '../facilitation/surfaceRegistry';
import VisionFacilitationScreen from '../facilitation/VisionFacilitationScreen';
import StreamMappingWorkspace from '../components/StreamMappingWorkspace';
import { useEngagement } from '../hooks/useEngagement';
import { useActivityFacilitation } from '../hooks/useActivityFacilitation';
import {
  ActivityAttachment,
  ActivityConversationPanel,
  type ActivityFacilitationBinding,
} from '../facilitation/ActivityConversation';
import { FacilitatorLauncher } from '../components/FacilitatorDock';
import { useSession } from '../hooks/useSession';
import { useSharedStream } from '../hooks/useSharedStream';
import { useStreamAssets } from '../hooks/useStreamAssets';
import StreamBusinessSequencePanel from '../components/StreamBusinessSequencePanel';
import StreamCurrentStateVsmPanel from '../components/StreamCurrentStateVsmPanel';
import { useKnowledgeFiles, type ActivityFiling } from '../hooks/useKnowledgeFiles';
import type { StreamWorkspaceStream } from '../components/streamMappingWorkspaceTypes';
import type {
  EngagementActivity,
  EngagementRole,
  EngagementSetup,
} from '../hooks/useEngagementSetup';
import {
  CURRENT_UNSUPPORTED_SPECIALIST_OPTION,
  ENGAGEMENT_SPECIALISTS,
  engagementActivityDestination,
  engagementSpecialistFor,
  engagementSpecialistLabel,
  normaliseEngagementSpecialistId,
} from './engagementSpecialists';
import './engagementSetup.css';

export interface EngagementSetupModel {
  setup: EngagementSetup | null;
  setSetup: React.Dispatch<React.SetStateAction<EngagementSetup | null>>;
  status: 'loading' | 'ready' | 'saving' | 'activating';
  error: { code: string; message: string; missing?: string[] } | null;
  activationState: string | null;
  loadLatest: () => Promise<void>;
  save: (next: EngagementSetup) => Promise<boolean>;
  activate: () => Promise<boolean>;
  change: (command: Record<string, unknown>) => Promise<boolean>;
  changeProcessSpine?: (
    command: Record<string, unknown> & { expectedRevision: number },
  ) => Promise<boolean>;
  ensureCharterSession: () => Promise<string | null>;
  ensureBriefSession?: () => Promise<string | null>;
  ensureActivitySession: (activityId: string, context?: { processId: string; specialistMethod: string; streamId?: string }) => Promise<string | null>;
  refreshQuietly?: () => Promise<void>;
  returnActivityOutput: (
    activityId: string,
    sessionId: string,
    output: { id: string; name: string; summary: string; sharedSource?: { streamId: string; streamRevision?: string; processRevisions: Record<string, string> } },
  ) => Promise<boolean>;
  completeActivity: (
    activityId: string,
    input: { result: string; notes?: string },
  ) => Promise<boolean>;
  // 090: where the completed activity's record landed in the knowledge base,
  // or that it could not be written. Optional so surfaces that never complete
  // an activity keep their existing shape.
  activityFiling?: { activityId: string; status: 'filed' | 'failed'; path: string; message?: string } | null;
}

const STEPS = ['Charter', 'People', 'Activities', 'Review'] as const;
type Step = typeof STEPS[number];

function uid(prefix: string): string {
  try {
    return `${prefix}-${crypto.randomUUID()}`;
  } catch {
    return `${prefix}-${Date.now().toString(36)}`;
  }
}

function displayDate(value?: string): string {
  if (!value) return 'date not entered';
  const parsed = new Date(`${value.length === 10 ? `${value}T00:00:00` : value}`);
  return Number.isNaN(parsed.getTime())
    ? value
    : new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
      .format(parsed);
}

function briefStatus(state: EngagementSetup['charter']['purpose']['state']): string {
  if (state === 'agreed') return 'Agreed';
  if (state === 'provisional') return 'Draft';
  return 'Not entered';
}

function changeStatus(status: unknown): string {
  if (status === 'pending') return 'Waiting for decision';
  if (status === 'agreedOutsideArcPilot') return 'Agreed';
  if (status === 'rejected') return 'Rejected';
  if (status === 'withdrawn') return 'Withdrawn';
  if (status === 'corrected') return 'Corrected';
  return 'Recorded';
}

function activityStatus(state: EngagementActivity['state']): string {
  return state === 'complete' ? 'Complete' : 'Planned';
}

function roleName(type: EngagementRole['role']): string {
  if (type === 'consultantLead') return 'Consultant lead';
  if (type === 'clientSponsor') return 'Client sponsor';
  return 'Client coordinator';
}

function role(setup: EngagementSetup, type: EngagementRole['role']): EngagementRole {
  return setup.roles.find((item) => item.role === type) || {
    id: uid('role'),
    role: type,
    name: '',
    title: '',
    contact: '',
    linkedUserId: null,
  };
}

function complete(setup: EngagementSetup, step: Step): boolean {
  if (step === 'Charter') return Boolean(setup.charter.purpose.value.trim());
  if (step === 'People') {
    return Boolean(
      role(setup, 'consultantLead').name.trim()
      && role(setup, 'clientSponsor').name.trim(),
    );
  }
  if (step === 'Activities') {
    return setup.activities.some((activity) => (
      activity.name.trim()
      && activity.purpose.trim()
      && activity.owner.trim()
      && activity.expectedOutput.trim()
    ));
  }
  return Boolean(
    setup.agreement.state === 'agreedOutsideArcPilot'
    && setup.agreement.recordedBy?.trim()
    && setup.agreement.decidedAt?.trim(),
  );
}

function ready(setup: EngagementSetup): boolean {
  return STEPS.every((step) => complete(setup, step));
}

function SaveStatus({ status }: { status: EngagementSetupModel['status'] }) {
  return (
    <span className="eng-setup-save" role="status" aria-live="polite">
      {status === 'saving'
        ? 'Saving...'
        : status === 'activating'
          ? 'Starting engagement...'
          : status === 'loading'
            ? 'Opening...'
          : 'Saved'}
    </span>
  );
}

function ErrorNotice({ model }: { model: EngagementSetupModel }) {
  if (!model.error) return null;
  const conflict = model.error.code === 'ENGAGEMENT_SETUP_CONFLICT';
  const recovery = model.error.code === 'ENGAGEMENT_ACTIVATION_RECOVERY_REQUIRED';
  const activationFailure = recovery
    || model.error.code === 'ENGAGEMENT_STORAGE_FAILURE'
    || model.error.code === 'ENGAGEMENT_SETUP_INCOMPLETE';
  return (
    <div className="eng-setup-alert" role="alert">
      <strong>{recovery
        ? 'Setup needs another try'
        : activationFailure
          ? 'Engagement could not start'
          : conflict
            ? 'This engagement changed since you opened it'
            : 'Unable to save'}</strong>
      <span>{model.error.message}</span>
      {model.error.missing?.length ? (
        <ul>
          {[...new Set(model.error.missing.map((item) => {
            if (item === 'charter.purpose') return 'Engagement brief';
            if (item === 'roles.consultantLead') return 'Consultant lead';
            if (item === 'roles.clientSponsor') return 'Client sponsor';
            if (item.startsWith('activities.')) return 'Complete activity details';
            if (item === 'activities') return 'At least one activity';
            if (item.startsWith('agreement.')) return 'Sponsor decision';
            return 'Required engagement information';
          }))].map((item) => <li key={item}>{item}</li>)}
        </ul>
      ) : null}
      <button type="button" onClick={() => void (
        activationFailure ? model.activate() : model.loadLatest()
      )}>
        {recovery ? 'Retry setup' : activationFailure ? 'Try starting again' : conflict ? 'Load latest' : 'Retry'}
      </button>
    </div>
  );
}

function CharterStep({ model, onContinue }: {
  model: EngagementSetupModel;
  onContinue: () => void;
}) {
  const setup = model.setup as EngagementSetup;
  const screenRevision = useRef(0);
  // 100, decision 10: there is one way to open this conversation and it is
  // simply there. The four brief fields and the shared composer show together
  // from the moment the step opens; an existing charter conversation is
  // resumed, and a new one is created by the FIRST MESSAGE, never by browsing.
  const facilitator = useActivityFacilitation({
    scopeKey: 'engagement-charter',
    ensureSession: () => model.ensureCharterSession(),
  });
  const facilitation: ActivityFacilitationBinding = {
    ...facilitator,
    sessionId: facilitator.sessionId || setup.charterFacilitationSessionId || '',
  };
  const conversationSessionId = facilitation.sessionId;
  // The one conversation reads the same before and after its session exists:
  // both take their wording from the charter surface's own definition.
  const charterDefinition = surfaceForId('engagement-charter-surface');
  const charterSurface = {
    label: charterDefinition?.chatLabel,
    emptyText: charterDefinition?.emptyChatText,
  };
  const setItem = (key: keyof EngagementSetup['charter'], value: string) => {
    screenRevision.current += 1;
    model.setSetup({
      ...setup,
      charter: {
        ...setup.charter,
        [key]: {
          ...setup.charter[key],
          value,
          state: value.trim() ? 'provisional' : 'unknown',
        },
      },
    });
  };
  const fields: Array<[keyof EngagementSetup['charter'], string, boolean]> = [
    ['purpose', 'What does the client want to achieve?', true],
    ['whyNow', 'Why does this need to happen now?', false],
    ['success', 'How will the client judge success?', false],
    ['scopeAndConstraints', 'What is in or out of scope?', false],
  ];
  const attachment = attachFacilitator({
    screenId: 'engagement-charter-surface',
    profileId: 'engagement-charter',
    resolveSession: () => conversationSessionId,
    getContext: () => ({
      revision: screenRevision.current,
      focus: null,
      values: Object.fromEntries(fields.map(([key]) => [key, setup.charter[key].value])),
    }),
    allowedCapabilityIds: [
      'engagement-charter.propose_item',
      'engagement-charter.apply_proposal',
      'engagement-charter.dismiss_proposal',
      'engagement-charter.set_item',
      'engagement-charter.close_session',
    ],
    actions: {
      proposals: {
        applyCapabilityId: 'engagement-charter.apply_proposal',
        dismissCapabilityId: 'engagement-charter.dismiss_proposal',
        applyLabel: 'Add to brief',
        dismissLabel: 'Dismiss',
      },
    },
    onCapabilityAcknowledged: ({ capabilityId, target }) => {
      const slot = target?.content.slot;
      const value = target?.content.text;
      if (capabilityId !== 'engagement-charter.apply_proposal'
          || typeof slot !== 'string' || typeof value !== 'string'
          || !fields.some(([key]) => key === slot)) return;
      model.setSetup((current) => current ? {
        ...current,
        charter: {
          ...current.charter,
          [slot]: { ...current.charter[slot as keyof EngagementSetup['charter']], value, state: 'provisional' },
        },
      } : current);
    },
  });
  return (
    <ActivityAttachment facilitation={facilitation} attachment={attachment}>
    <section className="eng-setup-section" aria-labelledby="charter-heading">
      <div className="eng-setup-section-head">
        <div>
          <span className="eng-setup-kicker">Engagement brief</span>
          <h2 id="charter-heading">Describe the engagement</h2>
          <p>Record what the client wants to achieve, why now, how they will judge success, and what is in or out of scope.</p>
        </div>
      </div>
      <div className="eng-charter-workspace eng-charter-workspace--open">
        <div className="eng-charter-form">
          <div className="eng-charter-grid">
            {fields.map(([key, label, required]) => (
              <label key={key} className="eng-field">
                <span>
                  {label}{required ? ' *' : ''}
                  <em data-state={setup.charter[key].state}>{briefStatus(setup.charter[key].state)}</em>
                </span>
                <textarea
                  rows={key === 'scopeAndConstraints' ? 3 : 2}
                  value={setup.charter[key].value}
                  onChange={(event) => setItem(key, event.target.value)}
                  placeholder={required ? 'Enter the main result the client wants.' : 'Enter this when known.'}
                />
              </label>
            ))}
          </div>
          <div className="eng-setup-actions">
            <button type="button" className="eng-button eng-button--primary" onClick={onContinue}>
              Continue: People
            </button>
          </div>
        </div>
        {/* The one dock and the one chat panel, in the step's own conversation
            column: the not-yet-opened conversation before the first message,
            the canonical companion after it. Closing after a failure returns
            to the composer, so what was said is not lost. */}
        <div id="engagement-charter-facilitator" className="eng-charter-conversation">
          <ActivityConversationPanel
            facilitation={facilitation}
            onClose={facilitator.close}
            {...charterSurface}
          />
        </div>
      </div>
    </section>
    </ActivityAttachment>
  );
}

function PeopleStep({ model, onBack, onContinue }: {
  model: EngagementSetupModel;
  onBack: () => void;
  onContinue: () => void;
}) {
  const setup = model.setup as EngagementSetup;
  const setRole = (type: EngagementRole['role'], patch: Partial<EngagementRole>) => {
    const current = role(setup, type);
    const others = setup.roles.filter((item) => item.role !== type);
    model.setSetup({ ...setup, roles: [...others, { ...current, ...patch }] });
  };
  const rows: Array<[EngagementRole['role'], string, boolean]> = [
    ['consultantLead', 'Consultant lead', true],
    ['clientSponsor', 'Client sponsor', true],
    ['clientCoordinator', 'Client coordinator', false],
  ];
  return (
    <section className="eng-setup-section" aria-labelledby="people-heading">
      <div className="eng-setup-section-head">
        <div>
          <span className="eng-setup-kicker">People</span>
          <h2 id="people-heading">Assign the people responsible</h2>
          <p>Add the consultant lead and client sponsor. These names do not give anyone access to ArcPilot.</p>
        </div>
      </div>
      <div className="eng-people-list">
        {rows.map(([type, label, required]) => {
          const item = role(setup, type);
          return (
            <div className="eng-person-row" key={type}>
              <label className="eng-field">
                <span>{label}{required ? ' *' : ''}</span>
                <input
                  value={item.name}
                  onChange={(event) => setRole(type, { name: event.target.value })}
                  placeholder={required ? 'Name' : 'Optional'}
                />
              </label>
              <label className="eng-field">
                <span>Role or title</span>
                <input
                  value={item.title || ''}
                  onChange={(event) => setRole(type, { title: event.target.value })}
                  placeholder="Optional"
                />
              </label>
              <label className="eng-field">
                <span>Contact</span>
                <input
                  value={item.contact || ''}
                  onChange={(event) => setRole(type, { contact: event.target.value })}
                  placeholder="Optional"
                />
              </label>
            </div>
          );
        })}
      </div>
      <div className="eng-setup-actions">
        <button type="button" className="eng-button" onClick={onBack}>Back</button>
        <button type="button" className="eng-button eng-button--primary" onClick={onContinue}>
          Continue: Activities
        </button>
      </div>
    </section>
  );
}

const emptyActivity = (): EngagementActivity => ({
  id: uid('activity'),
  plan: 'engagement',
  name: '',
  purpose: '',
  owner: '',
  expectedOutput: '',
  participants: [],
  dependencies: [],
  specialistMethod: null,
  state: 'planned',
  order: 0,
});

function ActivityEditor({ activity, setup, onChange, onDone }: {
  activity: EngagementActivity;
  setup: EngagementSetup;
  onChange: (next: EngagementActivity) => void;
  onDone?: () => void;
}) {
  const specialistId = normaliseEngagementSpecialistId(activity.specialistMethod) || '';
  const specialist = engagementSpecialistFor(specialistId);
  const pickerValue = specialistId && !specialist
    ? CURRENT_UNSUPPORTED_SPECIALIST_OPTION
    : specialistId;
  return (
    <div className="eng-activity-editor">
      <label className="eng-field">
        <span>Activity name *</span>
        <input value={activity.name} onChange={(event) => onChange({ ...activity, name: event.target.value })} />
      </label>
      <label className="eng-field eng-field--wide">
        <span>What will this activity achieve? *</span>
        <textarea rows={2} value={activity.purpose} onChange={(event) => onChange({ ...activity, purpose: event.target.value })} />
      </label>
      <label className="eng-field">
        <span>Owner *</span>
        <input value={activity.owner} onChange={(event) => onChange({ ...activity, owner: event.target.value })} />
      </label>
      <label className="eng-field">
        <span>What should this activity produce or decide? *</span>
        <input value={activity.expectedOutput} onChange={(event) => onChange({ ...activity, expectedOutput: event.target.value })} />
      </label>
      <label className="eng-field">
        <span>Participants</span>
        <input
          value={activity.participants.join(', ')}
          onChange={(event) => onChange({
            ...activity,
            participants: event.target.value.split(',').map((value) => value.trim()).filter(Boolean),
          })}
          placeholder="Names separated by commas"
        />
      </label>
      <label className="eng-field">
        <span>Must happen after</span>
        <select
          value={activity.dependencies[0] || ''}
          onChange={(event) => onChange({
            ...activity,
            dependencies: event.target.value ? [event.target.value] : [],
          })}
        >
          <option value="">Nothing</option>
          {setup.activities.filter((item) => item.id !== activity.id).map((item) => (
            <option key={item.id} value={item.id}>{item.name || 'Untitled activity'}</option>
          ))}
        </select>
      </label>
      <label className="eng-field">
        <span>Specialist workspace</span>
        <select
          value={pickerValue}
          onChange={(event) => onChange({
            ...activity,
            specialistMethod: event.target.value === CURRENT_UNSUPPORTED_SPECIALIST_OPTION
              ? activity.specialistMethod
              : event.target.value || null,
          })}
        >
          <option value="">No specialist workspace</option>
          {!specialist && specialistId ? (
            <option value={CURRENT_UNSUPPORTED_SPECIALIST_OPTION}>Unavailable workspace (choose another)</option>
          ) : null}
          {ENGAGEMENT_SPECIALISTS.map((definition) => (
            <option key={definition.id} value={definition.id}>{definition.label}</option>
          ))}
        </select>
      </label>
      {onDone ? (
        <div className="eng-activity-editor-actions">
          <button type="button" className="eng-button eng-button--primary" onClick={onDone}>Done</button>
        </div>
      ) : null}
    </div>
  );
}

function ActivitiesStep({ model, onBack, onContinue }: {
  model: EngagementSetupModel;
  onBack: () => void;
  onContinue: () => void;
}) {
  const setup = model.setup as EngagementSetup;
  const [editingId, setEditingId] = useState<string | null>(null);
  const update = (next: EngagementActivity) => {
    model.setSetup({
      ...setup,
      activities: setup.activities.map((item) => item.id === next.id ? next : item),
    });
  };
  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= setup.activities.length) return;
    const activities = [...setup.activities];
    [activities[index], activities[target]] = [activities[target], activities[index]];
    model.setSetup({ ...setup, activities: activities.map((item, order) => ({ ...item, order })) });
  };
  return (
    <section className="eng-setup-section" aria-labelledby="activities-heading">
      <div className="eng-setup-section-head">
        <div>
          <span className="eng-setup-kicker">Activity plan</span>
          <h2 id="activities-heading">Add the first activities</h2>
          <p>Add the work agreed for the start of the engagement. You can change the plan later.</p>
        </div>
        <button
          type="button"
          className="eng-button"
          onClick={() => {
            const activity = { ...emptyActivity(), order: setup.activities.length };
            model.setSetup({ ...setup, activities: [...setup.activities, activity] });
            setEditingId(activity.id);
          }}
        >
          <Plus size={16} aria-hidden="true" /> Add an activity
        </button>
      </div>
      <div className="eng-activity-list">
        {setup.activities.length === 0 && (
          <div className="eng-empty">
            <strong>No activities added</strong>
            <span>Add at least one activity with an owner and expected result.</span>
          </div>
        )}
        {setup.activities.map((activity, index) => (
          <article className="eng-activity-card" key={activity.id}>
            {editingId === activity.id ? (
              <ActivityEditor
                activity={activity}
                setup={setup}
                onChange={update}
                onDone={() => setEditingId(null)}
              />
            ) : (
              <>
                <div>
                  <span className="eng-activity-number">{index + 1}</span>
                  <span className="eng-state">Draft</span>
                  <h3>{activity.name || 'Untitled activity'}</h3>
                  <p>{activity.purpose || 'Result not entered'}</p>
                  <dl>
                    <div><dt>Owner</dt><dd>{activity.owner || 'Not assigned'}</dd></div>
                    <div><dt>Expected result</dt><dd>{activity.expectedOutput || 'Not entered'}</dd></div>
                    <div><dt>Participants</dt><dd>{activity.participants.join(', ') || 'Not added'}</dd></div>
                  </dl>
                </div>
                <div className="eng-row-actions">
                  <button type="button" onClick={() => move(index, -1)} disabled={index === 0}>Move up</button>
                  <button type="button" onClick={() => move(index, 1)} disabled={index === setup.activities.length - 1}>Move down</button>
                  <button type="button" onClick={() => setEditingId(activity.id)}>Edit</button>
                  <button
                    type="button"
                    onClick={() => model.setSetup({
                      ...setup,
                      activities: setup.activities
                        .filter((item) => item.id !== activity.id)
                        .map((item, order) => ({ ...item, order })),
                    })}
                  >
                    Remove
                  </button>
                </div>
              </>
            )}
          </article>
        ))}
      </div>
      <div className="eng-setup-actions">
        <button type="button" className="eng-button" onClick={onBack}>Back</button>
        <button type="button" className="eng-button eng-button--primary" onClick={onContinue}>
          Continue: Review
        </button>
      </div>
    </section>
  );
}

function ReviewStep({ model, onBack, onEdit }: {
  model: EngagementSetupModel;
  onBack: () => void;
  onEdit: (step: Step) => void;
}) {
  const setup = model.setup as EngagementSetup;
  const lead = role(setup, 'consultantLead');
  const sponsor = role(setup, 'clientSponsor');
  const checklist: Array<[string, boolean]> = [
    ['Brief', Boolean(setup.charter.purpose.value.trim())],
    ['Lead', Boolean(lead.name.trim())],
    ['Sponsor', Boolean(sponsor.name.trim())],
    ['Owned activity', complete(setup, 'Activities')],
    ['Agreement', complete(setup, 'Review')],
  ];
  const updateAgreement = (patch: Partial<EngagementSetup['agreement']>) => {
    model.setSetup({ ...setup, agreement: { ...setup.agreement, ...patch } });
  };
  if (model.activationState === 'finishing') {
    return (
      <section className="eng-setup-section eng-finishing" aria-live="polite">
        <span className="eng-setup-kicker">Finishing setup</span>
        <h2>Your engagement is active</h2>
        <p>ArcPilot is finishing setup. The engagement is active and your information is safe. The overview will open automatically.</p>
        <button type="button" className="eng-button eng-button--primary" disabled>
          Finishing setup...
        </button>
      </section>
    );
  }
  if (model.activationState === 'recovery-required') {
    return (
      <section className="eng-setup-section eng-finishing" aria-live="polite">
        <span className="eng-setup-kicker">Setup needs another try</span>
        <h2>Your engagement is active</h2>
        <p>ArcPilot could not finish setup. The engagement is active and your information is safe. Try again.</p>
      </section>
    );
  }
  return (
    <section className="eng-setup-section" aria-labelledby="review-heading">
      <div className="eng-setup-section-head">
        <div>
          <span className="eng-setup-kicker">Review and start</span>
          <h2 id="review-heading">Review before starting</h2>
          <p>Check the brief, people, activities and sponsor decision.</p>
          <p>Starting the engagement makes these activities active.</p>
        </div>
      </div>
      <div className="eng-review-grid">
        <article>
          <span>Brief</span>
          <h3>{setup.charter.purpose.value || 'Main result not entered'}</h3>
          <p>Why now: {briefStatus(setup.charter.whyNow.state)}</p>
          <button type="button" onClick={() => onEdit('Charter')}>Edit brief</button>
        </article>
        <article>
          <span>People</span>
          <h3>Lead: {lead.name || 'Not entered'}</h3>
          <p>Sponsor: {sponsor.name || 'Not entered'}</p>
          <button type="button" onClick={() => onEdit('People')}>Edit people</button>
        </article>
        <article>
          <span>Activities</span>
          <h3>{setup.activities.length} {setup.activities.length === 1 ? 'activity' : 'activities'}</h3>
          <p>First: {setup.activities[0]?.name || 'Not entered'}</p>
          <button type="button" onClick={() => onEdit('Activities')}>Edit activities</button>
        </article>
      </div>
      <fieldset className="eng-agreement">
        <legend>Sponsor agreement</legend>
        <label>
          <input
            type="radio"
            name="agreement"
            checked={setup.agreement.state === 'notReviewed'}
            onChange={() => updateAgreement({ state: 'notReviewed' })}
          />
          Not yet reviewed
        </label>
        <label>
          <input
            type="radio"
            name="agreement"
            checked={setup.agreement.state === 'agreedOutsideArcPilot'}
            onChange={() => updateAgreement({
              state: 'agreedOutsideArcPilot',
              recordedBy: lead.name,
              recordedAt: new Date().toISOString(),
            })}
          />
          Agreed outside ArcPilot
        </label>
        <label>
          <input
            type="radio"
            name="agreement"
            checked={setup.agreement.state === 'revisionRequested'}
            onChange={() => updateAgreement({ state: 'revisionRequested' })}
          />
          Revision requested
        </label>
        {setup.agreement.state === 'agreedOutsideArcPilot' && (
          <div className="eng-agreement-detail">
            <p>Record a decision made outside ArcPilot. This is a note, not a verified digital approval.</p>
            <label className="eng-field">
              <span>Decision date *</span>
              <input type="date" value={setup.agreement.decidedAt || ''} onChange={(event) => updateAgreement({ decidedAt: event.target.value })} />
            </label>
            <label className="eng-field">
              <span>Note or source</span>
              <input value={setup.agreement.note || ''} onChange={(event) => updateAgreement({ note: event.target.value })} />
            </label>
            <span>Recorded by {lead.name || 'the consultant lead'}</span>
          </div>
        )}
      </fieldset>
      <div className="eng-ready">
        <strong>{ready(setup) ? 'Ready to start' : 'Complete the required items to start'}</strong>
        <ul>
          {checklist.map(([label, done]) => (
            <li key={label} data-complete={done}><Check size={14} aria-hidden="true" /> {label}</li>
          ))}
        </ul>
      </div>
      <div className="eng-setup-actions">
        <button type="button" className="eng-button" onClick={onBack}>Back</button>
        <button
          type="button"
          className="eng-button eng-button--primary"
          disabled={!ready(setup) || model.status === 'activating'}
          onClick={async () => {
            if (!(await model.save(setup))) return;
            await model.activate();
          }}
        >
          {model.status === 'activating' ? 'Starting engagement...' : 'Start engagement'}
        </button>
      </div>
    </section>
  );
}

export function EngagementSetupWorkspace({ model, engagementName }: {
  model: EngagementSetupModel;
  engagementName: string;
}) {
  const [step, setStep] = useState<Step>('Charter');
  const navigate = useNavigate();
  if (model.status === 'loading' || !model.setup) {
    return <div className="eng-setup-loading" role="status">Opening engagement...</div>;
  }
  const setup = model.setup;
  const go = async (next: Step) => {
    if (await model.save(setup)) setStep(next);
  };
  return (
    <div className="eng-setup-page">
      <header className="eng-setup-header">
        <div>
          <span className="eng-setup-kicker">Engagement <b>Draft</b></span>
          <h1>Set up engagement</h1>
          <p>{engagementName}</p>
        </div>
        <div className="eng-setup-header-actions">
          <SaveStatus status={model.status} />
          <button
            type="button"
            className="eng-button"
            onClick={async () => {
              if (await model.save(setup)) navigate('/');
            }}
          >
            Save and leave
          </button>
        </div>
      </header>
      <ErrorNotice model={model} />
      <div className="eng-setup-layout">
        <nav className="eng-setup-steps" aria-label="Engagement setup steps">
          {STEPS.map((item, index) => (
            <button
              type="button"
              key={item}
              aria-current={step === item ? 'step' : undefined}
              data-complete={complete(setup, item)}
              onClick={() => setStep(item)}
            >
              <span>{index + 1}</span>
              <strong>{item === 'Charter' ? 'Brief' : item}</strong>
              <small>{complete(setup, item) ? 'Complete' : item === 'Review' ? 'Needs agreement' : 'Needs input'}</small>
            </button>
          ))}
        </nav>
        {step === 'Charter' && <CharterStep model={model} onContinue={() => void go('People')} />}
        {step === 'People' && <PeopleStep model={model} onBack={() => setStep('Charter')} onContinue={() => void go('Activities')} />}
        {step === 'Activities' && <ActivitiesStep model={model} onBack={() => setStep('People')} onContinue={() => void go('Review')} />}
        {step === 'Review' && <ReviewStep model={model} onBack={() => setStep('Activities')} onEdit={setStep} />}
      </div>
    </div>
  );
}

export function EngagementActivationRecovery({ model, engagementName }: {
  model: EngagementSetupModel;
  engagementName: string;
}) {
  const finishing = model.activationState === 'finishing';
  return (
    <div className="eng-active-page">
      <header className="eng-active-header">
        <div>
          <span className="eng-setup-kicker">
            {finishing ? 'Finishing setup' : 'Setup needs another try'}
          </span>
          <h1>Your engagement is active</h1>
          <p>{engagementName}</p>
        </div>
      </header>
      <section className="eng-setup-section eng-finishing" aria-live="polite">
        <p>
          {finishing
            ? 'ArcPilot is finishing setup. The engagement is active and your information is safe. The overview will open automatically.'
            : 'ArcPilot could not finish setup. The engagement is active and your information is safe. Try again.'}
        </p>
        {finishing ? (
          <button type="button" className="eng-button eng-button--primary" disabled>
            Finishing setup...
          </button>
        ) : (
          <button
            type="button"
            className="eng-button eng-button--primary"
            onClick={() => void model.activate()}
          >
            Retry setup
          </button>
        )}
      </section>
    </div>
  );
}

export function EngagementActiveOverview({ setup, engagementName }: {
  setup: EngagementSetup;
  engagementName: string;
}) {
  const completed = new Set(
    setup.activities.filter((activity) => activity.state === 'complete').map(({ id }) => id),
  );
  const next = setup.activities.find((activity) => (
    activity.state !== 'complete'
    && activity.dependencies.every((dependencyId) => completed.has(dependencyId))
  ));
  const sponsor = setup.roles.find((item) => item.role === 'clientSponsor');
  return (
    <div className="eng-active-page">
      <header className="eng-active-header">
        <div>
          <span className="eng-setup-kicker">Engagement <b>Active</b></span>
          <h1>Overview</h1>
          <p>{engagementName}</p>
        </div>
        <Link className="eng-button" to="/engagement/manage">Manage engagement</Link>
      </header>
      {next ? (
        <section className="eng-next">
          <span>Next activity</span>
          <h2>{next.name}</h2>
          <p>{next.purpose}</p>
          <div>
            <strong>Owner: {next.owner}</strong>
            <span>Expected: {next.expectedOutput}</span>
            <Link className="eng-button eng-button--light" to={engagementActivityDestination(next)}>
              Open activity
            </Link>
          </div>
        </section>
      ) : (
        <section className="eng-next eng-next--empty">
          <span>No activity is ready</span>
          <h2>Check completed and blocked activities</h2>
          <p>Every activity is complete or waiting for another activity to finish.</p>
          <Link className="eng-button eng-button--light" to="/engagement/manage">Manage activity plan</Link>
        </section>
      )}
      <div className="eng-overview-grid">
        <section className="eng-panel">
          <span className="eng-setup-kicker">Activity plan</span>
          <h2>{setup.activities.length} {setup.activities.length === 1 ? 'activity' : 'activities'}</h2>
          {setup.activities.slice(0, 4).map((activity) => (
            <article className="eng-plan-row" key={activity.id}>
              <span className="eng-state">{activityStatus(activity.state)}</span>
              <strong>{activity.name}</strong>
              <small>{activity.owner} · {activity.expectedOutput}</small>
            </article>
          ))}
          <Link to="/engagement/activities">View all activities</Link>
        </section>
        <section className="eng-panel">
          <span className="eng-setup-kicker">Engagement brief</span>
          <h2>{setup.charter.purpose.value || 'Main result not entered'}</h2>
          <p>Sponsor: {sponsor?.name || 'Not entered'}</p>
          <p>Agreement recorded {displayDate(setup.agreement.decidedAt)} outside ArcPilot</p>
          <Link to="/engagement/manage">View brief and history</Link>
        </section>
      </div>
    </div>
  );
}

export function EngagementActivities({ setup }: { setup: EngagementSetup }) {
  return (
    <div className="eng-active-page">
      <header className="eng-active-header">
        <div><span className="eng-setup-kicker">Activity plan</span><h1>Activities</h1><p>Work agreed for this engagement.</p></div>
        <Link className="eng-button" to="/engagement/manage">Manage activity plan</Link>
      </header>
      <section className="eng-panel">
        <ul className="eng-activity-list" aria-label="Activities">
          {/* 100, decision 6: the Brief is an activity. It is listed here and
              opens in the same shell, with no plan record of its own. */}
          <li className="eng-activity-card eng-activity-card--active">
            <div>
              <span className="eng-state">Always available</span>
              <h2>Brief</h2>
              <p>What the client wants, why now, and the activities it pulls in.</p>
            </div>
            <Link className="eng-button" to="/engagement/brief">Open Brief</Link>
          </li>
          {setup.activities.map((activity, index) => (
            <li className="eng-activity-card eng-activity-card--active" key={activity.id}>
              <div>
                <span className="eng-activity-number">{index + 1}</span>
                <span className="eng-state">{activityStatus(activity.state)}</span>
                <h2>{activity.name}</h2>
                <p>{activity.purpose}</p>
                <dl>
                  <div><dt>Owner</dt><dd>{activity.owner}</dd></div>
                  <div><dt>Expected result</dt><dd>{activity.expectedOutput}</dd></div>
                </dl>
              </div>
              <Link className="eng-button" to={engagementActivityDestination(activity)}>
                Open activity
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

export function EngagementActivityWorkspace({ model, activityId }: {
  model: EngagementSetupModel;
  activityId: string;
}) {
  const setup = model.setup as EngagementSetup;
  const activity = setup.activities.find((candidate) => candidate.id === activityId);
  const [result, setResult] = useState('');
  const [notes, setNotes] = useState('');
  const [completing, setCompleting] = useState(false);
  const { refileActivity } = useKnowledgeFiles();
  const [retryFiling, setRetryFiling] = useState<ActivityFiling | null>(null);
  const [retrying, setRetrying] = useState(false);
  if (!activity) {
    return <div className="eng-active-page"><div role="alert">Activity not found.</div></div>;
  }
  const specialist = engagementSpecialistFor(activity.specialistMethod);
  const unsupportedSpecialist = Boolean(activity.specialistMethod) && !specialist;
  const completedFiling = model.activityFiling && model.activityFiling.activityId === activity.id
    ? model.activityFiling
    : null;
  const filing: ActivityFiling | null = retryFiling || completedFiling;
  const filedPath = filing && filing.path ? filing.path : (activity.filedPath || '');
  return (
    <div className="eng-active-page">
      <header className="eng-active-header">
        <div>
          <Link className="eng-back" to="/engagement/activities"><ChevronLeft size={16} /> Activities</Link>
          <span className="eng-setup-kicker">Engagement activity</span>
          <h1>{activity.name}</h1>
          <p>{activity.purpose}</p>
        </div>
      </header>
      <ErrorNotice model={model} />
      {unsupportedSpecialist ? (
        <section className="eng-panel">
          <h2>Specialist workspace unavailable</h2>
          <p>
            This activity uses a specialist workspace that is no longer available.
            Choose another workspace in the activity plan.
          </p>
          <Link className="eng-button" to="/engagement/manage">Edit activity</Link>
        </section>
      ) : null}
      <div className="eng-overview-grid">
        <section className="eng-setup-section" aria-labelledby="activity-do-heading">
          <div className="eng-setup-section-head">
            <div>
              <span className="eng-setup-kicker">Do the activity</span>
              <h2 id="activity-do-heading">Record what this activity concluded</h2>
              <p>Owner: {activity.owner} · Produces: {activity.expectedOutput}</p>
            </div>
          </div>
          {specialist ? (
            <div className="eng-agreement-detail" style={{ gridTemplateColumns: '1fr' }}>
              <p>
                {specialist.label} is an optional accelerator for this activity.
                Reviewed output will appear under Evidence &amp; findings.
              </p>
              <div className="eng-setup-actions" style={{ marginTop: 0, borderTop: 0, paddingTop: 0 }}>
                <Link className="eng-button" to={engagementActivityDestination(activity)}>
                  Launch {specialist.label}
                </Link>
              </div>
            </div>
          ) : null}
          <label className="eng-field">
            <span>Result *</span>
            <textarea
              rows={3}
              value={result}
              onChange={(event) => setResult(event.target.value)}
              placeholder="What did this activity conclude?"
            />
          </label>
          <label className="eng-field">
            <span>Working notes</span>
            <textarea
              rows={3}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Optional detail for later reference."
            />
          </label>
          <div className="eng-setup-actions">
            <button
              type="button"
              className="eng-button eng-button--primary"
              disabled={completing || !result.trim()}
              onClick={async () => {
                setCompleting(true);
                await model.completeActivity(activity.id, {
                  result: result.trim(),
                  notes: notes.trim(),
                });
                setCompleting(false);
              }}
            >
              {completing ? 'Saving...' : 'Mark complete'}
            </button>
          </div>
        </section>
        <section className="eng-panel">
          <span className="eng-setup-kicker">Assets</span>
          <h2>{activity.state === 'complete' ? 'Captured from ' : 'Will be captured from '}{activity.name}</h2>
          <article className="eng-plan-row">
            <span className="eng-state"><FileText size={13} aria-hidden="true" /></span>
            <strong>Activity record</strong>
            <small>Result, working notes, owner and status</small>
          </article>
          <article className="eng-plan-row">
            <span className="eng-state"><FileText size={13} aria-hidden="true" /></span>
            <strong>Captured facts</strong>
            <small>Facts recorded during this activity, if any</small>
          </article>
          {filing && filing.status === 'failed' ? (
            <div className="eng-filing eng-filing--failed" role="alert">
              <p>Could not file this record. Your work is saved.</p>
              <button
                type="button"
                className="eng-button"
                disabled={retrying}
                onClick={async () => {
                  setRetrying(true);
                  const retried = await refileActivity(activity.id);
                  setRetrying(false);
                  if (retried.ok) setRetryFiling(retried.filing);
                }}
              >
                {retrying ? 'Retrying...' : 'Retry'}
              </button>
            </div>
          ) : (
            <div className="eng-filing" role="status">
              <p>In the knowledge base</p>
              <small>This page is refreshed as the activity changes.</small>
              <Link to={filedPath ? `/engagement/files/${filedPath}` : '/engagement/files'}>
                Open in Files
              </Link>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export function EngagementValueStreamMappingSession({ model, activity, sessionId, streamOverride }: {
  model: EngagementSetupModel;
  activity: EngagementActivity;
  sessionId: string;
  streamOverride?: StreamWorkspaceStream;
}) {
  const navigate = useNavigate();
  const { currentEngagementId } = useEngagement();
  const session = useSession(sessionId, 'discovery', streamOverride?.sharedProcesses ? { contextId: 'shared-process-vsm' } : undefined);
  const shared = useSharedStream(currentEngagementId, streamOverride?.sharedProcesses ? streamOverride.id : null);
  const stream: StreamWorkspaceStream = streamOverride || {
    id: activity.id,
    name: activity.name,
    oneLiner: activity.purpose,
    smes: activity.participants.map((name) => ({ name })),
    mappingSessionId: sessionId,
  };
  const [returning, setReturning] = useState(false);
  const summary = stream.sharedProcesses
    ? shared.shared?.processes?.map(process => `${process.name}\n${process.flowText || ''}`).join('\n\n') || ''
    : session.newModel.flowText.trim();
  return (
    <div className="journey-specialist-workspace journey-specialist-workspace--contextual">
      <div className="journey-specialist-context" aria-label="Activity context">
        <div>
          <span>Activity</span>
          <strong>{activity.name}</strong>
        </div>
        <div className="journey-specialist-context__actions">
          <Link to={`/engagement/activities/${encodeURIComponent(activity.id)}`}>Back to activity</Link>
          <button
            type="button"
            className="journey-button journey-button--primary"
            disabled={returning || session.sessionLoading || !summary}
            onClick={async () => {
              setReturning(true);
              const returned = await model.returnActivityOutput(activity.id, sessionId, {
                id: uid('output'),
                name: activity.expectedOutput,
                summary,
                ...(stream.sharedProcesses && shared.shared ? { sharedSource: {
                  streamId: stream.id, streamRevision: shared.shared.streamRevision,
                  processRevisions: Object.fromEntries((shared.shared.processes || []).map(process => [process.processId, process.revision])),
                } } : {}),
              });
              setReturning(false);
              if (returned) navigate('/engagement/evidence');
            }}
          >
            {returning ? 'Saving...' : 'Save output and finish'}
          </button>
        </div>
      </div>
      <StreamMappingWorkspace streamOverride={stream} sessionOverride={session} />
    </div>
  );
}

export function EngagementValueStreamMappingWorkspace({ model, activityId }: {
  model: EngagementSetupModel;
  activityId: string;
}) {
  const setup = model.setup as EngagementSetup;
  const activity = setup.activities.find((candidate) => candidate.id === activityId);
  const linked = setup.activitySessions.find((candidate) => (
    candidate.activityId === activityId
    && candidate.specialistId === normaliseEngagementSpecialistId(activity?.specialistMethod)
  ));
  const { currentEngagementId } = useEngagement();
  const assets = useStreamAssets(currentEngagementId, linked?.sessionId);
  const context = `${currentEngagementId || ''}:${activityId}`;
  const facilitator = useActivityFacilitation({ scopeKey: context, engagementKey: currentEngagementId || '', ensureSession: () => model.ensureActivitySession(activityId) });
  const { sessionId } = facilitator;
  const originalStream = assets.streams.find(stream => stream.id === activityId || (linked?.sessionId && stream.mappingSessionId === linked.sessionId));
  if (!activity || engagementSpecialistFor(activity.specialistMethod)?.id !== 'value-stream-mapping') {
    return <NavigateToActivity activityId={activityId} />;
  }
  if (!sessionId) {
    const original = originalStream || { id: activity.id, name: activity.name, oneLiner: activity.purpose, smes: [] };
    return <main className="business-workspace">
      <Link to={`/engagement/activities/${encodeURIComponent(activityId)}`}>Back to activity</Link>
      <h1>{activity.name}</h1><p>Original value stream mapping work. Browsing does not start a session.</p>
      <Link to={`/engagement/business-map?originActivity=${encodeURIComponent(activityId)}`}>Business map</Link>
      <FacilitatorLauncher {...facilitator} onOpen={facilitator.open} />
      {assets.error ? <p role="alert">{assets.error}</p> : null}
      <StreamBusinessSequencePanel stream={original} session={assets.session} readOnly />
      <StreamCurrentStateVsmPanel stream={original} session={assets.session} readOnly />
    </main>;
  }
  return (
    <EngagementValueStreamMappingSession
      key={sessionId}
      model={model}
      activity={activity}
      sessionId={sessionId}
      streamOverride={originalStream}
    />
  );
}

function EngagementClientVisionSession({ model, activity, facilitation }: {
  model: EngagementSetupModel;
  activity: EngagementActivity;
  facilitation: ActivityFacilitationBinding;
}) {
  const sessionId = facilitation.sessionId;
  const navigate = useNavigate();
  const [summary, setSummary] = useState('');
  const [returning, setReturning] = useState(false);
  return (
    <div className="journey-specialist-workspace journey-specialist-workspace--contextual">
      <div className="journey-specialist-context" aria-label="Activity context">
        <div>
          <span>Activity</span>
          <strong>{activity.name}</strong>
        </div>
        <div className="journey-specialist-context__actions">
          <Link to={`/engagement/activities/${encodeURIComponent(activity.id)}`}>Back to activity</Link>
        </div>
      </div>
      <VisionFacilitationScreen facilitation={facilitation} />
      <section className="eng-panel">
        <label className="eng-field">
          <span>Reviewed summary</span>
          <textarea
            rows={4}
            value={summary}
            onChange={(event) => setSummary(event.target.value)}
            placeholder="Enter the approved result of this activity."
          />
        </label>
        <div className="eng-setup-actions">
          <button
            type="button"
            className="eng-button eng-button--primary"
            disabled={returning || !summary.trim() || !sessionId}
            onClick={async () => {
              setReturning(true);
              const returned = await model.returnActivityOutput(activity.id, sessionId, {
                id: uid('output'),
                name: activity.expectedOutput,
                summary: summary.trim(),
              });
              setReturning(false);
              if (returned) navigate('/engagement/evidence');
            }}
          >
            {returning ? 'Saving...' : 'Save output and finish'}
          </button>
        </div>
      </section>
    </div>
  );
}

export function EngagementClientVisionWorkspace({ model, activityId }: {
  model: EngagementSetupModel;
  activityId: string;
}) {
  const setup = model.setup as EngagementSetup;
  const activity = setup.activities.find((candidate) => candidate.id === activityId);
  const linked = setup.activitySessions.find((candidate) => (
    candidate.activityId === activityId
    && candidate.specialistId === normaliseEngagementSpecialistId(activity?.specialistMethod)
  ));
  const { currentEngagementId } = useEngagement();
  const facilitator = useActivityFacilitation({ scopeKey: `${currentEngagementId}/${activityId}/vision`, engagementKey: currentEngagementId || '', ensureSession: () => model.ensureActivitySession(activityId) });
  // 100, decision 10: an existing vision conversation is resumed; a new one is
  // created by the first message, not by opening the screen.
  const facilitation: ActivityFacilitationBinding = {
    ...facilitator,
    sessionId: facilitator.sessionId || linked?.sessionId || '',
  };
  if (!activity || engagementSpecialistFor(activity.specialistMethod)?.id !== 'client-vision') {
    return <NavigateToActivity activityId={activityId} />;
  }
  return (
    <EngagementClientVisionSession
      key={facilitation.sessionId}
      model={model}
      activity={activity}
      facilitation={facilitation}
    />
  );
}

function NavigateToActivity({ activityId }: { activityId: string }) {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(`/engagement/activities/${encodeURIComponent(activityId)}`, { replace: true });
  }, [activityId, navigate]);
  return <div className="eng-setup-loading" role="status">Opening activity...</div>;
}

export function EngagementEvidenceAndFindings({ setup }: { setup: EngagementSetup }) {
  return (
    <div className="eng-active-page">
      <header className="eng-active-header"><div><span className="eng-setup-kicker">Activity outputs</span><h1>Evidence &amp; findings</h1><p>Outputs approved in an activity appear here.</p></div></header>
      <section className="eng-panel">
        {setup.activityOutputs.length === 0 ? (
          <div className="eng-empty"><strong>No reviewed outputs yet</strong><span>Complete an activity and save its output.</span></div>
        ) : setup.activityOutputs.map((output) => (
          <article className="eng-change-row" key={output.id}>
            <span className="eng-state">Reviewed output</span>
            <strong>{output.name}</strong>
            <p>{output.summary}</p>
            <small>
              Source activity: {setup.activities.find(
                (activity) => activity.id === output.source.activityId,
              )?.name || 'Activity no longer in the plan'}
            </small>
          </article>
        ))}
      </section>
    </div>
  );
}

export function EngagementDecisions({ setup }: { setup: EngagementSetup }) {
  const pending = setup.changes.filter((change) => change.status === 'pending');
  return (
    <div className="eng-active-page">
      <header className="eng-active-header"><div><span className="eng-setup-kicker">Changes</span><h1>Decisions</h1><p>Changes waiting for a decision appear here.</p></div></header>
      <section className="eng-panel">
        {pending.length === 0 ? <div className="eng-empty"><strong>No decisions waiting</strong><span>Changes that need a decision will appear here.</span></div> : pending.map((change) => (
          <article className="eng-change-row" key={String(change.id)}><span className="eng-state">Decision needed</span><strong>{String((change.target as { field?: string } | undefined)?.field || change.type)}</strong><p>{String(change.reason)}</p><Link to="/engagement/manage">Review change</Link></article>
        ))}
      </section>
    </div>
  );
}

// Feature 071: direct editing. Every editor below applies its change
// immediately through `model.change` with one of the six direct-apply
// command types (editCharterField / editRoleField / editActivityField /
// addActivity / removeActivity / reorderActivity) — no reason field, no
// Correct-vs-Request-change choice, no Decisions queue.

function ManageBriefTab({ model }: { model: EngagementSetupModel }) {
  const setup = model.setup as EngagementSetup;
  const [draft, setDraft] = useState(setup.charter);
  const fields: Array<[keyof EngagementSetup['charter'], string]> = [
    ['purpose', 'What does the client want to achieve?'],
    ['whyNow', 'Why does this need to happen now?'],
    ['success', 'How will the client judge success?'],
    ['scopeAndConstraints', 'What is in or out of scope?'],
  ];
  return (
    <section className="eng-panel">
      <div className="eng-charter-grid">
        {fields.map(([key, label]) => (
          <label key={key} className="eng-field">
            <span>{label}</span>
            <textarea
              rows={key === 'scopeAndConstraints' ? 3 : 2}
              value={draft[key].value}
              onChange={(event) => setDraft({
                ...draft,
                [key]: { ...draft[key], value: event.target.value },
              })}
            />
          </label>
        ))}
      </div>
      <div className="eng-setup-actions">
        <button
          type="button"
          className="eng-button eng-button--primary"
          onClick={() => {
            const keys = Object.keys(draft) as Array<keyof EngagementSetup['charter']>;
            for (const key of keys) {
              if (draft[key].value !== setup.charter[key].value) {
                void model.change({
                  type: 'editCharterField',
                  target: { kind: 'charter', field: key },
                  value: { value: draft[key].value, state: 'agreed' },
                });
              }
            }
          }}
        >
          Save changes
        </button>
      </div>
    </section>
  );
}

function ManagePeopleTab({ model }: { model: EngagementSetupModel }) {
  const setup = model.setup as EngagementSetup;
  const [draft, setDraft] = useState(setup.roles);
  const rows: Array<[EngagementRole['role'], string]> = [
    ['consultantLead', roleName('consultantLead')],
    ['clientSponsor', roleName('clientSponsor')],
    ['clientCoordinator', roleName('clientCoordinator')],
  ];
  const roleFor = (type: EngagementRole['role']): EngagementRole => (
    draft.find((item) => item.role === type) || {
      id: '', role: type, name: '', title: '', contact: '', linkedUserId: null,
    }
  );
  const setField = (type: EngagementRole['role'], field: 'name' | 'title' | 'contact', fieldValue: string) => {
    setDraft((current) => (
      current.some((item) => item.role === type)
        ? current.map((item) => (item.role === type ? { ...item, [field]: fieldValue } : item))
        : [...current, { id: uid('role'), role: type, name: '', title: '', contact: '', linkedUserId: null, [field]: fieldValue }]
    ));
  };
  return (
    <section className="eng-panel">
      <div className="eng-people-list">
        {rows.map(([type, label]) => {
          const item = roleFor(type);
          return (
            <div className="eng-person-row" key={type}>
              <label className="eng-field">
                <span>{label}</span>
                <input value={item.name} onChange={(event) => setField(type, 'name', event.target.value)} />
              </label>
              <label className="eng-field">
                <span>Role or title</span>
                <input value={item.title || ''} onChange={(event) => setField(type, 'title', event.target.value)} />
              </label>
              <label className="eng-field">
                <span>Contact</span>
                <input value={item.contact || ''} onChange={(event) => setField(type, 'contact', event.target.value)} />
              </label>
            </div>
          );
        })}
      </div>
      <div className="eng-setup-actions">
        <button
          type="button"
          className="eng-button eng-button--primary"
          onClick={() => {
            for (const original of setup.roles) {
              const edited = draft.find((item) => item.id === original.id);
              if (!edited) continue;
              for (const field of ['name', 'title', 'contact'] as const) {
                if ((edited[field] || '') !== (original[field] || '')) {
                  void model.change({
                    type: 'editRoleField',
                    target: { kind: 'role', id: original.id, field },
                    value: edited[field] || '',
                  });
                }
              }
            }
          }}
        >
          Save changes
        </button>
      </div>
    </section>
  );
}

function activityFieldDiffs(before: EngagementActivity, after: EngagementActivity): Array<[string, unknown]> {
  const fields: Array<keyof EngagementActivity> = [
    'name', 'purpose', 'owner', 'expectedOutput', 'participants', 'dependencies', 'specialistMethod',
  ];
  return fields
    .filter((field) => JSON.stringify(before[field]) !== JSON.stringify(after[field]))
    .map((field) => [field, after[field]]);
}

function activityHasRecordedOutput(setup: EngagementSetup, activity: EngagementActivity): boolean {
  return activity.state === 'complete'
    || setup.activityOutputs.some((output) => output.source.activityId === activity.id);
}

function ManageActivityPlanTab({ model }: { model: EngagementSetupModel }) {
  const setup = model.setup as EngagementSetup;
  const [addingActivity, setAddingActivity] = useState<EngagementActivity | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<EngagementActivity | null>(null);
  const move = (activity: EngagementActivity, targetIndex: number) => {
    if (targetIndex < 0 || targetIndex >= setup.activities.length) return;
    void model.change({
      type: 'reorderActivity',
      target: { kind: 'activity', id: activity.id, field: 'order' },
      value: targetIndex,
    });
  };
  const remove = (activity: EngagementActivity) => {
    if (activityHasRecordedOutput(setup, activity)
      && !window.confirm(`"${activity.name || 'This activity'}" has a recorded output. Remove it anyway?`)) {
      return;
    }
    void model.change({
      type: 'removeActivity',
      target: { kind: 'activity', id: activity.id, field: 'remove' },
      value: null,
    });
  };
  return (
    <section className="eng-panel">
      <div className="eng-panel-action">
        <button
          type="button"
          className="eng-button"
          onClick={() => setAddingActivity({ ...emptyActivity(), order: setup.activities.length })}
        >
          <Plus size={16} aria-hidden="true" /> Add an activity
        </button>
      </div>
      {addingActivity ? (
        <section className="eng-change-composer" aria-labelledby="add-activity-heading">
          <span className="eng-setup-kicker">Activity plan</span>
          <h2 id="add-activity-heading">Add an activity</h2>
          <ActivityEditor
            activity={addingActivity}
            setup={setup}
            onChange={setAddingActivity}
          />
          <div className="eng-setup-actions">
            <button type="button" className="eng-button" onClick={() => setAddingActivity(null)}>Cancel</button>
            <button
              type="button"
              className="eng-button eng-button--primary"
              disabled={!complete({ ...setup, activities: [addingActivity] }, 'Activities')}
              onClick={() => {
                void model.change({
                  type: 'addActivity',
                  target: { kind: 'activityPlan', id: addingActivity.id, field: 'add' },
                  value: addingActivity,
                });
                setAddingActivity(null);
              }}
            >
              Add activity
            </button>
          </div>
        </section>
      ) : null}
      {setup.activities.map((activity, index) => (
        <article className="eng-manage-row" key={activity.id}>
          {editingId === activity.id && editDraft ? (
            <ActivityEditor
              activity={editDraft}
              setup={setup}
              onChange={setEditDraft}
              onDone={() => {
                const diffs = activityFieldDiffs(activity, editDraft);
                for (const [field, fieldValue] of diffs) {
                  void model.change({
                    type: 'editActivityField',
                    target: { kind: 'activity', id: activity.id, field },
                    value: fieldValue,
                  });
                }
                setEditingId(null);
                setEditDraft(null);
              }}
            />
          ) : (
            <>
              <div>
                <span>{activity.owner} · {activity.expectedOutput}{activity.specialistMethod ? ` · ${engagementSpecialistLabel(activity.specialistMethod)}` : ''}</span>
                <strong>{activity.name || 'Untitled activity'}</strong>
              </div>
              <div className="eng-row-actions">
                <button type="button" disabled={index === 0} onClick={() => void move(activity, index - 1)}>Move up</button>
                <button type="button" disabled={index === setup.activities.length - 1} onClick={() => void move(activity, index + 1)}>Move down</button>
                <button type="button" onClick={() => { setEditingId(activity.id); setEditDraft(activity); }}>Edit</button>
                <button type="button" onClick={() => void remove(activity)}>Remove</button>
              </div>
            </>
          )}
        </article>
      ))}
      <p className="eng-muted" style={{ marginTop: 14 }}>
        Edits apply immediately — no approval step. Every change is recorded in Change history.
      </p>
    </section>
  );
}

function ManageHistoryTab({ setup }: { setup: EngagementSetup }) {
  return (
    <section className="eng-panel">
      {setup.changes.length === 0 ? (
        <div className="eng-empty">
          <strong>No changes recorded</strong>
          <span>Direct edits will appear here as they are made.</span>
        </div>
      ) : setup.changes.map((change) => {
        const target = change.target as { field?: string; id?: string; kind?: string } | undefined;
        const recordedBy = change.recordedBy ? String(change.recordedBy) : 'Unknown';
        // A still-pending legacy entry has not been recorded as applied — show
        // who raised it without implying a settled "when" the way an applied
        // or decided entry's humanised recordedAt does.
        const recordedAt = change.status !== 'pending' && typeof change.recordedAt === 'string'
          ? displayDate(change.recordedAt)
          : null;
        return (
          <article className="eng-change-row" key={String(change.id)}>
            <span className="eng-state">{changeStatus(change.status)}</span>
            <strong>{String(target?.field || change.type)}</strong>
            <p>By {recordedBy}{recordedAt ? ` · ${recordedAt}` : ''}</p>
          </article>
        );
      })}
    </section>
  );
}

export function EngagementManage({ model, engagementName }: {
  model: EngagementSetupModel;
  engagementName: string;
}) {
  const setup = model.setup as EngagementSetup;
  const [tab, setTab] = useState<'brief' | 'people' | 'activities' | 'history'>('brief');
  return (
    <div className="eng-active-page">
      <header className="eng-active-header">
        <div><Link to="/engagement" className="eng-back"><ChevronLeft size={16} /> Overview</Link><h1>Manage engagement</h1><p>{engagementName}</p></div>
        <div className="eng-setup-header-actions">
          <Link className="eng-button" to="/knowledge-repo">Knowledge repository</Link>
          <span className="eng-state">Active</span>
        </div>
      </header>
      <ErrorNotice model={model} />
      <nav className="eng-manage-tabs" aria-label="Manage engagement">
        {(['brief', 'people', 'activities', 'history'] as const).map((item) => (
          <button type="button" key={item} aria-current={tab === item ? 'page' : undefined} onClick={() => setTab(item)}>
            {item === 'activities'
              ? 'Activity plan'
              : item === 'history'
                ? 'Change history'
                : item === 'brief'
                  ? 'Brief'
                  : 'People'}
          </button>
        ))}
      </nav>
      {tab === 'brief' ? <ManageBriefTab key={tab} model={model} /> : null}
      {tab === 'people' ? <ManagePeopleTab key={tab} model={model} /> : null}
      {tab === 'activities' ? <ManageActivityPlanTab key={tab} model={model} /> : null}
      {tab === 'history' ? <ManageHistoryTab setup={setup} /> : null}
    </div>
  );
}
